import React from "react";

interface HeaderItemProps {
  id: string;
  label: string;
  show: boolean;
}

/**
 * A sidebar group label — "Applications", "Taxonomy", "Data Import".
 *
 * Until 2026-08-30 this was `<li>` → `<Link to="#">` → `<label>`. Three things were
 * wrong with that, and all three are fixed here. See `okf/templates/e2e.md` § 18.
 *
 * - **The link went nowhere.** Eight of these render on every authenticated page, so
 *   a keyboard user tabbed through eight focusable elements whose only effect was to
 *   push `#` onto history. A group label is not interactive; it is now a plain block.
 * - **`<label>` with no `for` labels nothing.** The element is now the group's
 *   accessible name, referenced by `aria-labelledby` from the nested `<ul>` that
 *   `Sidebar` wraps around the group's items — so a screen reader announces
 *   "Applications, list of 3" instead of one flat list of 26 mixed things.
 * - **The type token sat on an inline `<label>`.** The 12px text therefore lived in a
 *   line box struck at the inherited 16px, and the row measured **29.61px** for a
 *   12px label. The token is on the block now. Same trap, same fix, as the workspace
 *   breadcrumb's column headings — `okf/common/e2e.md` § 13.
 *
 * Colour moved off `lightslategray` (#8c919a, **3.17:1** on white — under AA for
 * 12px text) onto the `ink-tertiary` role (#6c778b, 4.52:1).
 *
 * `tw-ps-2` is unchanged, deliberately: the headers hang to the left of the item
 * labels, and that indent difference is the grouping cue.
 */
const HeaderItem: React.FC<HeaderItemProps> = ({ id, label, show }) => {
  return (
    <div id={id}>
      {show ? (
        <div
          id={`${id}-label`}
          className={`tw-py-1 tw-ps-2 tw-text-sub-title-label tw-text-ink-tertiary`}
        >
          {label}
        </div>
      ) : (
        <div id={`${id}-spacer`} className={`tw-py-2`}></div>
      )}
    </div>
  );
};

export default HeaderItem;
